'use client';

import { useEffect, useTransition } from 'react';
import { useSetAtom } from 'jotai';
import { WalletEntrance } from '@/features/wallet/components/wallet-entrance';
import UserinfoEffect from '@/features/auth/components/userinfo-effect';
import PathHistoryEffects from '@/components/path-history-effects';
import { AudioProvider } from '@/components/providers/audio-provider';
import { usePathname } from '@/i18n/navigation';
import { useHeartbeat } from '@/hooks/use-heartbeat';
import { useEdgeSwipeNavigation } from '@/hooks/use-edge-swipe-navigation';
import { OAuthCallbackHandler } from '@/components/auth/oauth-callback-handler';
import { isAndroid } from '@/utils/browser';
import Script from 'next/script';
import { loadGlobalConfig, normalizefooterAnimateConfig } from '@/libs/global-config';
import { footerAnimateConfigAtom } from '@/store/global-config';

interface SharedLayoutProps {
  children: React.ReactNode;
}

/**
 * 共享布局组件
 * 包含所有页面都需要的基础功能：
 * - WalletEntrance: 钱包入口组件
 * - UserinfoEffect: 用户信息效果组件
 * - VisitorEffects: 访客效果组件
 * - PathHistoryEffects: 路径历史记录组件
 * - TransactionEffects: 交易效果组件（充值/提现）
 * - Heartbeat: 心跳打点功能
 * - EdgeSwipeNavigation: 滑动导航（仅 App 内）
 */
export default function SharedLayout({ children }: SharedLayoutProps) {
  const pathname = usePathname();
  const [, startTransition] = useTransition();
  const setfooterAnimateConfig = useSetAtom(footerAnimateConfigAtom);

  // 判断是否为登录页面
  const isLoginPage = pathname === '/login';

  // 心跳打点
  useHeartbeat();

  // 滑动导航（仅在 App 内生效）
  useEdgeSwipeNavigation();

  // 低优先级加载全局动态配置（traceUserIds 等），不阻塞首屏渲染
  useEffect(() => {
    startTransition(async () => {
      const config = await loadGlobalConfig();
      const footerConfig = config?.footerAnimateConfig ?? config?.footerApngConfig;
      setfooterAnimateConfig(normalizefooterAnimateConfig(footerConfig));
    });
  }, [setfooterAnimateConfig, startTransition]);

  // 安卓系统下拦截 PWA 安装提示，避免浏览器底部显示"安装应用"引导
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!isAndroid()) return;

    const handler = (e: Event) => {
      e.preventDefault();
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  return (
    <AudioProvider>
      {children}

      {/* OAuth回调处理 - 全局 */}
      <OAuthCallbackHandler />

      {/* 钱包相关组件 - 登录页面不显示 */}
      {!isLoginPage && <WalletEntrance />}

      {/* 用户信息相关效果 - 登录页面不显示 */}
      {!isLoginPage && <UserinfoEffect />}

      {/* 路径历史记录 - 全局记录 */}
      <PathHistoryEffects />
      <Script id="facebook-pixel">
        {`
          !(function(f, b, e, v, n, t, s) {
            if (f.fbq) return;
            n = f.fbq = function() {
              n.callMethod
                ? n.callMethod.apply(n, arguments)
                : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = !0;
            n.version = "2.0";
            n.queue = [];
            t = b.createElement(e);
            t.async = !0;
            t.src = v;
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
          })(
            window,
            document,
            "script",
            "https://connect.facebook.net/en_US/fbevents.js"
          );
        `}
      </Script>
    </AudioProvider>
  );
}
